import _ from 'underscore';

import {assert} from 'Asserts';
import {STAGE_RUN_STATUS} from 'workflows/constants/StageRunConstants';
import {
  ExperimentalConditionValue,
  StageConfiguration,
  StageEntrySummary,
  StageFlowNode,
  StageRun,
} from 'workflows/WorkflowConstants';

/**
 * Abstract class representing a graph of nodes, where each node has a non-negative integer 'tag'.
 * In practice, 'node' can refer to a StageFlowNode, or it can refer to a stage run or
 * aggregated stage run, and 'tag' refers to the index of the containing stage.
 */
export abstract class TaggedNodeGraph {
  constructor() {
    // Memoized functions

    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.isValidNodeId = _.memoize(this.isValidNodeId);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getNodeIds = _.memoize(this.getNodeIds);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getParentNodeIds = _.memoize(this.getParentNodeIds);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getTag = _.memoize(this.getTag);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getNodeIds = _.memoize(this.getNodeIds);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getPrimaryChildNodeIds = _.memoize(this.getPrimaryChildNodeIds);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getSecondaryChildNodeIds = _.memoize(this.getSecondaryChildNodeIds);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getInitialNodeIds = _.memoize(this.getInitialNodeIds);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getPrimaryAncestorNodeIds = _.memoize(this.getPrimaryAncestorNodeIds);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.isLastDescendantWithTag = _.memoize(this.isLastDescendantWithTag);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getSiblingsAboveNode = _.memoize(this.getSiblingsAboveNode);
  }

  isValidNodeId(nodeId: string): boolean {
    return this.getNodeIds().includes(nodeId);
  }

  getNodeIds(): Array<string> {
    throw new Error('Must be implemented by a subclass.');
  }

  getParentNodeIds(nodeId: string): Array<string> {
    throw new Error('Must be implemented by a subclass.');
  }

  getTag(nodeId: string): number {
    throw new Error('Must be implemented by a subclass.');
  }

  getTags(): Array<number> {
    throw new Error('Must be implemented by a subclass.');
  }

  getPrimaryParentNodeId(nodeId: string): string {
    return this.getParentNodeIds(nodeId)[0];
  }

  getNodeIdsByTag(tag: number): Array<string> {
    return this.getNodeIds().filter((nodeId) => this.getTag(nodeId) === tag);
  }

  getPrimaryChildNodeIds(nodeId: string): Array<string> {
    return this.getNodeIds().filter((childNodeId) => this.getPrimaryParentNodeId(childNodeId) === nodeId);
  }

  getSecondaryChildNodeIds(nodeId: string): Array<string> {
    return this.getNodeIds().filter((childNodeId) =>
      this.getParentNodeIds(childNodeId).slice(1).includes(nodeId)
    );
  }

  getInitialNodeIds(): Array<string> {
    return this.getNodeIds().filter((nodeId) => this.getParentNodeIds(nodeId).length === 0);
  }

  /**
   * Return a list of IDs of all primary ancestors, including the node itself.
   */
  getPrimaryAncestorNodeIds(nodeId: string): Array<string> {
    const parentNodeId = this.getPrimaryParentNodeId(nodeId);
    if (parentNodeId) {
      return [...this.getPrimaryAncestorNodeIds(parentNodeId), nodeId];
    } else {
      return [nodeId];
    }
  }

  /**
   * When the tag represents the stage index, this method returns whether the node is the last
   * descendant in the stage.
   */
  isLastDescendantWithTag(nodeId: string): boolean {
    const currentTag = this.getTag(nodeId);
    return this.getPrimaryChildNodeIds(nodeId).every(
      (childNodeId) => this.getTag(childNodeId) !== currentTag
    );
  }

  /**
   * Return all primary and non-primary descendants.
   */
  getDescendantNodeIds(nodeId: string): Array<string> {
    return this.getNodeIds().filter((descendantNodeId) => this.isDescendant(descendantNodeId, nodeId));
  }

  /**
   * Check if node1 is the descendant of node2 via a chain of primary parentage.
   */
  isPrimaryDescendant(node1Id: string, node2Id: string): boolean {
    if (node1Id === node2Id) {
      return true;
    }

    const parentNodeId = this.getPrimaryParentNodeId(node1Id);
    if (!parentNodeId) {
      return false;
    }

    return this.isPrimaryDescendant(parentNodeId, node2Id);
  }

  isDescendant(node1Id: string, node2Id: string): boolean {
    if (node1Id === node2Id) {
      return true;
    }

    return this.getParentNodeIds(node1Id).some((parentNodeId) => this.isDescendant(parentNodeId, node2Id));
  }

  getSiblingsAboveNode(nodeId: string): Array<string> {
    const parentNodeId = this.getPrimaryParentNodeId(nodeId);
    const siblingIds = parentNodeId ? this.getPrimaryChildNodeIds(parentNodeId) : this.getInitialNodeIds();
    const siblingIndex = siblingIds.indexOf(nodeId);
    assert(siblingIndex > -1, 'Node not found in array of siblings');
    return siblingIds.slice(0, siblingIndex);
  }

  /**
   * Return a list of objects of the form {parentId, childId}, where one may be null, representing
   * all links where parent tag <= tag < child tag. Only primary links are included.
   *
   * This can be visualized as all links crossing the right boundary of a stage, and is analogous to
   * _get_links_across_stage_configuration.
   */
  getLinksAcrossTag(tag: number): Array<{parentId: string | null; childId: string | null}> {
    const nonNullParentLinks = [];
    for (const smallerTag of _.range(tag + 1)) {
      for (const nodeId of this.getNodeIdsByTag(smallerTag)) {
        const primaryChildNodeIds = this.getPrimaryChildNodeIds(nodeId);
        if (primaryChildNodeIds.length === 0) {
          nonNullParentLinks.push({parentId: nodeId, childId: null});
        } else {
          for (const primaryChildNodeId of primaryChildNodeIds) {
            if (this.getTag(primaryChildNodeId) > tag) {
              nonNullParentLinks.push({parentId: nodeId, childId: primaryChildNodeId});
            }
          }
        }
      }
    }
    const nullParentLinks = this.getInitialNodeIds()
      .filter((nodeId) => this.getTag(nodeId) > tag)
      .map((nodeId) => ({parentId: null, childId: nodeId}));
    return [...nonNullParentLinks, ...nullParentLinks];
  }
}

export class StageFlowNodeGraph extends TaggedNodeGraph {
  stageFlowNodes: Array<StageFlowNode>;
  stageConfigurations: Array<StageConfiguration>;

  constructor(stageFlowNodes: Array<StageFlowNode>, stageConfigurations: Array<StageConfiguration>) {
    super();
    this.stageFlowNodes = stageFlowNodes;
    this.stageConfigurations = stageConfigurations;

    // Memoized functions

    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getStageConfiguration = _.memoize(this.getStageConfiguration);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getStageFlowNode = _.memoize(this.getStageFlowNode);
  }

  // Override superclass method
  override getNodeIds(): Array<string> {
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(this.stageFlowNodes, 'id');
  }

  // Override superclass method
  override getParentNodeIds(nodeId: string): Array<string> {
    return this.getStageFlowNode(nodeId).parentIds;
  }

  // Override superclass method
  override getTag(nodeId: string): number {
    return this.getStageConfiguration(nodeId).index;
  }

  // Override superclass method
  override getTags(): Array<number> {
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(this.stageConfigurations, 'index');
  }

  getStageConfiguration(nodeId: string): StageConfiguration {
    const stageConfiguration = this.stageConfigurations.find((configuration) =>
      // eslint-disable-next-line no-restricted-syntax
      _.pluck(configuration.stageFlowNodes, 'id').includes(nodeId)
    );
    assert(stageConfiguration != null, `No stage configuration found corresponding to node ${nodeId}`, {
      stageConfigurations: this.stageConfigurations,
    });
    return stageConfiguration;
  }

  getStageFlowNode(nodeId: string): StageFlowNode {
    const stageFlowNode = _.findWhere(this.stageFlowNodes, {id: nodeId});
    assert(stageFlowNode != null, `No stage flow node found corresponding to node ${nodeId}`);
    return stageFlowNode;
  }

  getName(nodeId: string): null {
    return null;
  }

  getExperimentalConditionValues(nodeId: string): Array<ExperimentalConditionValue> {
    return this.getStageFlowNode(nodeId).experimentalConditionValues;
  }

  getStatus(nodeId: string): null {
    return null;
  }
}

export class StageRunGraph extends TaggedNodeGraph {
  stageRuns: Array<StageRun>;
  stageFlowNodeGraph: StageFlowNodeGraph;

  constructor(stageRuns: Array<StageRun>, stageConfigurations: Array<StageConfiguration>) {
    super();
    this.stageRuns = _.sortBy(stageRuns, 'createdAt');
    this.stageFlowNodeGraph = new StageFlowNodeGraph(
      // eslint-disable-next-line no-restricted-syntax
      _.pluck(stageRuns, 'stageFlowNode'),
      stageConfigurations
    );

    // Memoized functions

    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getStageFlowNodeId = _.memoize(this.getStageFlowNodeId);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getStageRunIdFromStageFlowNodeId = _.memoize(this.getStageRunIdFromStageFlowNodeId);
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.getStageRun = _.memoize(this.getStageRun);
  }

  // Override superclass method
  override getNodeIds(): Array<string> {
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(this.stageRuns, 'id');
  }

  // Override superclass method
  override getParentNodeIds(nodeId: string): Array<string> {
    return this.stageFlowNodeGraph
      .getParentNodeIds(this.getStageFlowNodeId(nodeId))
      .map((parentId) => this.getStageRunIdFromStageFlowNodeId(parentId));
  }

  // Override superclass method
  override getTag(nodeId: string): number {
    return this.stageFlowNodeGraph.getTag(this.getStageFlowNodeId(nodeId));
  }

  // Override superclass method
  override getTags(): Array<number> {
    return this.stageFlowNodeGraph.getTags();
  }

  getStageFlowNodeId(nodeId: string): string {
    return this.getStageRun(nodeId).stageFlowNode.id;
  }

  getStageRunIdFromStageFlowNodeId(stageFlowNodeId: string): string {
    const stageRun = this.stageRuns.find((run) => run.stageFlowNode.id === stageFlowNodeId);
    assert(stageRun != null, 'No stage run found corresponding to stage flow node', {
      numStageFlowNodes: this.stageFlowNodeGraph.stageFlowNodes.length,
      numStageRuns: this.stageRuns.length,
      stageFlowNodeId,
      stageFlowNodeGraphStageFlowNodes: this.stageFlowNodeGraph.stageFlowNodes,
      stageRuns: this.stageRuns,
    });
    return stageRun.id;
  }

  getStageRun(nodeId: string): StageRun {
    const stageRun = _.findWhere(this.stageRuns, {id: nodeId});
    assert(stageRun != null, `No stage run found corresponding to node ${nodeId}`);
    return stageRun;
  }

  getName(nodeId: string): string {
    return this.getStageRun(nodeId).name;
  }

  getExperimentalConditionValues(nodeId: string): Array<ExperimentalConditionValue> {
    return this.getStageRun(nodeId).stageFlowNode.experimentalConditionValues;
  }

  getStatus(nodeId: string): STAGE_RUN_STATUS {
    return this.getStageRun(nodeId).status;
  }

  getDefaultStageEntry(stageIndex: number): StageEntrySummary | null {
    const stageRuns = this.getNodeIdsByTag(stageIndex).map((nodeId: string) => this.getStageRun(nodeId));
    return stageRuns.length > 0 ? stageRuns[0].stageEntry : null;
  }
}
